import React, { useEffect } from "react";
import { StyleSheet, ScrollView, View } from "react-native";
import { router, usePathname } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { EmailInput } from "@/components/ui/EmailInput";
import { RadioButtonGroup } from "@/components/ui/RadioButtonGroup";
import { DateInput } from "@/components/ui/DateInput";
import { useTheme } from "@/hooks/useTheme";
import { MaterialIcons } from "@expo/vector-icons";
import { useCallRequest } from "@/contexts/CallRequestContext";
import { formatPhoneNumber } from "@/utils/phoneValidation";

const STEP_INDEX = 0;

export default function JobDetailsScreen() {
  const { colors } = useTheme();
  const { formData, updateFormData, updateStep, hasSavedData, isLoading } = useCallRequest();
  const pathname = usePathname();

  const isOnThisScreen = pathname === "/call-request" || pathname.endsWith("/call-request/");

  // Redirect to welcome-back if there's saved data
  useEffect(() => {
    if (!isLoading && hasSavedData && isOnThisScreen) {
      router.replace("/call-request/welcome-back");
    }
  }, [isLoading, hasSavedData, isOnThisScreen]);

  // Track current step
  useEffect(() => {
    if (!isLoading && !hasSavedData && isOnThisScreen) {
      updateStep(STEP_INDEX);
    }
  }, [isLoading, hasSavedData, isOnThisScreen, updateStep]);

  const canContinue =
    formData.companyName.trim() !== "" && formData.submitterEmail.trim() !== "" && formData.jobName.trim() !== "";

  const handleContinue = () => {
    updateStep(1);
    router.push("/call-request/call-details");
  };

  const FieldLabel = ({ label, required }: { label: string; required?: boolean }) => (
    <View style={styles.fieldLabel}>
      <ThemedText style={[styles.label, { color: colors.text }]}>{label}</ThemedText>
      {required && (
        <ThemedText style={[styles.requiredText, { color: colors.ruby?.text || colors.textSecondary }]}>
          Required *
        </ThemedText>
      )}
    </View>
  );

  return (
    <ScrollView style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={styles.content}>
        <View style={styles.header}>
          <MaterialIcons name="work" size={40} color={colors.primary} />
          <ThemedText style={[styles.title, { color: colors.text }]}>Job Details</ThemedText>
          <ThemedText style={[styles.subtitle, { color: colors.textSecondary }]}>
            Tell us about the job you need staffing for
          </ThemedText>
        </View>

        <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <FieldLabel label="Company Name" required />
          <Input
            placeholder="Enter company name"
            value={formData.companyName}
            onChangeText={(text) => updateFormData({ companyName: text })}
            icon="business"
          />

          <View style={styles.spacer} />

          <FieldLabel label="Submitter Email" required />
          <EmailInput
            value={formData.submitterEmail}
            onChangeText={(text) => updateFormData({ submitterEmail: text })}
            placeholder="Enter your email address"
          />

          <View style={styles.spacer} />

          <FieldLabel label="Job Name" required />
          <Input
            placeholder="Enter job name"
            value={formData.jobName}
            onChangeText={(text) => updateFormData({ jobName: text })}
            icon="label"
          />

          <View style={[styles.divider, { backgroundColor: colors.border }]} />

          <View style={styles.dateRow}>
            <View style={styles.dateField}>
              <DateInput
                label="Job Start Date"
                value={formData.jobStartDate || null}
                mode="date"
                onChange={(date) => updateFormData({ jobStartDate: date.toISOString() })}
                placeholder="Start date"
              />
            </View>
            <View style={styles.dateField}>
              <DateInput
                label="Job End Date"
                value={formData.jobEndDate || null}
                mode="date"
                onChange={(date) => updateFormData({ jobEndDate: date.toISOString() })}
                placeholder="End date"
              />
            </View>
          </View>

          <View style={[styles.divider, { backgroundColor: colors.border }]} />

          <FieldLabel label="Venue Name" />
          <Input
            placeholder="Enter venue name"
            value={formData.venueName}
            onChangeText={(text) => updateFormData({ venueName: text })}
            icon="place"
          />

          <View style={styles.spacer} />

          <FieldLabel label="Venue Address" />
          <Input
            placeholder="Enter venue address"
            value={formData.venueAddress}
            onChangeText={(text) => updateFormData({ venueAddress: text })}
            icon="location-on"
          />

          <View style={[styles.divider, { backgroundColor: colors.border }]} />

          <FieldLabel label="Meal Option" />
          <Input
            placeholder="e.g., Boxed lunch, catered, none"
            value={formData.mealOption}
            onChangeText={(text) => updateFormData({ mealOption: text })}
            icon="restaurant"
          />

          <View style={styles.spacer} />

          <FieldLabel label="Indoor or Outdoor?" />
          <RadioButtonGroup
            options={["Indoor", "Outdoor", "Both"]}
            value={(formData.indoorOutdoor as any) || undefined}
            onChange={(item) => updateFormData({ indoorOutdoor: item })}
            orientation="horizontal"
            style={styles.toggle}
          />

          <View style={styles.spacer} />

          <FieldLabel label="Will team members travel to multiple locations?" />
          <RadioButtonGroup
            options={["Yes", "No"]}
            value={(formData.multipleLocations as any) || undefined}
            onChange={(item) => updateFormData({ multipleLocations: item })}
            orientation="horizontal"
            style={styles.toggle}
          />

          <View style={[styles.divider, { backgroundColor: colors.border }]} />

          <FieldLabel label="Preferred Uniform" />
          <Input
            placeholder="e.g., Black polo, all black"
            value={formData.preferredUniform}
            onChangeText={(text) => updateFormData({ preferredUniform: text })}
            icon="checkroom"
          />

          <View style={styles.spacer} />

          <FieldLabel label="Onsite Contact Name" />
          <Input
            placeholder="Enter contact name"
            value={formData.onsiteContactName}
            onChangeText={(text) => updateFormData({ onsiteContactName: text })}
            icon="person"
          />

          <View style={styles.spacer} />

          <FieldLabel label="Onsite Contact Phone Number" />
          <Input
            placeholder="(555) 555-5555"
            value={formData.onsiteContactPhone}
            onChangeText={(text) => updateFormData({ onsiteContactPhone: formatPhoneNumber(text) })}
            icon="phone"
            keyboardType="phone-pad"
          />

          <View style={[styles.divider, { backgroundColor: colors.border }]} />

          <FieldLabel label="Request specific team members?" />
          <RadioButtonGroup
            options={["Yes", "No"]}
            value={(formData.requestSpecificMembers as any) || undefined}
            onChange={(item) => updateFormData({ requestSpecificMembers: item })}
            orientation="horizontal"
            style={styles.toggle}
          />

          {formData.requestSpecificMembers === "Yes" && (
            <View style={styles.conditionalField}>
              <ThemedText style={[styles.noteText, { color: colors.textSecondary }]}>
                We can do our best to accommodate requests for specific team members but we cannot guarantee them.
              </ThemedText>
              <Input
                placeholder="Enter team member names or details"
                value={formData.specificMemberRequests}
                onChangeText={(text) => updateFormData({ specificMemberRequests: text })}
                multiline
                numberOfLines={3}
                containerStyle={styles.textAreaContainer}
                style={styles.textArea}
              />
            </View>
          )}
        </View>

        <Button
          text="Next: Call Details"
          icon="arrow-right"
          iconPosition="right"
          iconSize={18}
          iconColor="white"
          onPress={handleContinue}
          enabled={canContinue}
          style={[
            styles.continueButton,
            {
              backgroundColor: canContinue ? colors.primary : colors.border,
              opacity: canContinue ? 1 : 0.5,
            },
          ]}
          textStyle={styles.continueButtonText}
        />
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 20,
    gap: 24,
  },
  header: {
    alignItems: "center",
    gap: 12,
    marginBottom: 8,
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    textAlign: "center",
  },
  subtitle: {
    fontSize: 16,
    textAlign: "center",
    lineHeight: 24,
  },
  card: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 20,
  },
  fieldLabel: {
    gap: 4,
    marginBottom: 8,
  },
  label: {
    fontSize: 15,
    fontWeight: "600",
  },
  requiredText: {
    fontSize: 12,
  },
  spacer: {
    height: 16,
  },
  divider: {
    height: 1,
    marginVertical: 20,
  },
  dateRow: {
    flexDirection: "row",
    gap: 12,
  },
  dateField: {
    flex: 1,
  },
  toggle: {
    marginTop: 4,
  },
  conditionalField: {
    marginTop: 12,
    gap: 8,
  },
  noteText: {
    fontSize: 13,
    lineHeight: 18,
    fontStyle: "italic",
  },
  textAreaContainer: {
    height: "auto",
    minHeight: 80,
    alignItems: "flex-start",
    paddingVertical: 8,
  },
  textArea: {
    minHeight: 64,
    textAlignVertical: "top",
  },
  continueButton: {
    borderRadius: 8,
    minHeight: 52,
  },
  continueButtonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "600",
  },
});
